import { Program } from '@models/membership/Program'
import { programSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createProgram as createProgramAPI,
  deleteProgram as deleteProgramAPI,
  fetchProgramBySlug,
  fetchPrograms,
  updateProgram as updateProgramAPI,
} from '@services/membership/ProgramService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  getSlugOrModelSlug,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<Program> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'program',
  initialState,
  reducers: {
    ...baseReducers,
    setSelectedSlug: (state, action) => ({
      ...state,
      selectedSlug: action.payload,
    }),
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getProgramsSuccess,
  setQuery: setProgramQuery,
  reset: resetProgramState,
  resetQueryAndIds: resetProgramQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Program }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPrograms = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedProgram = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectProgramById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectProgramsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

export const selectProgramBySlug = (slug: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((program) => program.slug === slug)
  )

/**
 * Actions
 */

export const setSelectedProgram =
  (program: Program | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Program>(program)))

export const setSelectedProgramWithSlug =
  (program: Program | string) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedSlug(getSlugOrModelSlug<Program>(program))
    )

export const getPrograms = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchPrograms(params),
    [programSchema],
    slice.actions
  )

export const createProgram = (program: Program) =>
  SBAPI.CreateDispatched(
    createProgramAPI(program),
    programSchema,
    slice.actions
  )

export const getProgramWithSlug = (slug: string) =>
  SBAPI.FetchDispatched<Program>(
    fetchProgramBySlug(slug),
    programSchema,
    slice.actions
  )

export const updateProgram = (program: Program) =>
  SBAPI.UpdateDispatched<Program>(
    updateProgramAPI(program),
    program,
    slice.actions
  )

export const deleteProgram = (program: Program) =>
  SBAPI.DestroyDispatched<Program>(
    deleteProgramAPI(program),
    program,
    slice.actions
  )
